// Subscription machine for the Market Probabilities board -- the replacement for
// the retired client-side generic_pricer fan-out (use-pricing-fanout.ts). Instead
// of computing the touch-prob ladder call-by-call, the board now SUBSCRIBES to the
// backend `subscribe.market_probabilities` stream: one subscription per board
// token, each pushing that token's whole board (all expiries x models x levels)
// as async `update` messages the reducer folds in via SET_FEED.
//
// ROUTING (the load-bearing bit). Each subscribe ack returns a bare channel_id of
// `1`, so ALL of the per-token subscriptions collide on the same channel number.
// The shared WS router (handle-messages.ts) routes an `update` by matching
// activeChannels[key] === channel_id, and handleOpenChannelResponse refuses to
// record a second key for an already-seen channel result -- so only ONE of our
// keys is ever matched, and it receives EVERY token's push. We therefore do NOT
// rely on the channel_id to tell tokens apart: the handler is a SHARED demuxer
// registered under every key that routes on the PAYLOAD's own `token` field
// (parsed.token) and dispatches to that token's slot. This makes cross-wiring
// structurally impossible -- a BTC push carries token:"BTC" and can only ever
// update BTC's card, whichever key the router happened to match. The subscribe
// id must CONTAIN the SUBSCRIPTIONS_KEY.MARKET_PROBABILITIES substring or the ack
// never lands in activeChannels and no update ever routes (isSubscriptionKey).
import { useEffect, type MutableRefObject } from 'react';

import { SUBSCRIPTIONS_KEY } from '../../constants/websocket-events';
import { MARKETS } from './markets';
import { parseMarketProbabilitiesFeed } from './feed';
import type { MarketProbabilitiesAction } from './state';

// The per-token subscription key = request id. Contains the registered
// SUBSCRIPTIONS_KEY.MARKET_PROBABILITIES substring so the ack is recorded, and is
// suffixed with the token so each subscription registers its own handler slot.
export const feedChannelKey = (token: string): string =>
  `${SUBSCRIPTIONS_KEY.MARKET_PROBABILITIES}_${token}`;

// Warm-up window: how long we wait after subscribing for the FIRST board push
// before declaring the stream unavailable on this environment. On staging the
// method returns -32601 and no push ever arrives -- but the shared WS router
// swallows a subscribe error reply (handle-messages.ts routes subscription-key
// ids to handleOpenChannelResponse, which only acts on a numeric result), so the
// timeout is our observable signal for BOTH the errored and the silent-no-data
// cases. Generous enough that a healthy feed (pushes land in ~1-2s) always beats
// it, so the happy path is untouched.
export const FEED_WARMUP_TIMEOUT_MS = 18_000;

// Build the subscribe request for one token. Shape verified live on TEST.
export const buildFeedSubscribeRequest = (token: string): string =>
  JSON.stringify({
    jsonrpc: '2.0',
    id: feedChannelKey(token),
    method: 'subscribe.market_probabilities',
    params: { venue: 'deribit', symbol: token, kind: 'one_touch' },
  });

export interface UseMarketProbabilitiesFeedArgs {
  isConnected: boolean;
  sendMessage: ((message: string) => void) | undefined;
  subscribeToChannel: ((id: string, handler: (raw: unknown) => void) => void) | undefined;
  safeUnsubscribe: (reqId: string) => void;
  dispatchRef: MutableRefObject<React.Dispatch<MarketProbabilitiesAction>>;
}

export function useMarketProbabilitiesFeed({
  isConnected,
  sendMessage,
  subscribeToChannel,
  safeUnsubscribe,
  dispatchRef,
}: UseMarketProbabilitiesFeedArgs): void {
  useEffect(() => {
    if (!isConnected || !sendMessage || !subscribeToChannel) return;

    // Warm-up timer: if no board push has arrived by FEED_WARMUP_TIMEOUT_MS after
    // subscribing, declare the stream unavailable on this environment (calm
    // full-board empty-state). The first push clears it; the reducer's
    // no-downgrade guard means even a stray late fire can't blank a live board.
    const warmupTimer = window.setTimeout(() => {
      dispatchRef.current({ type: 'FEED_UNAVAILABLE', reason: 'timeout' });
    }, FEED_WARMUP_TIMEOUT_MS);

    // The shared demuxer: parse the push and route it to the token named in the
    // PAYLOAD (never the subscription key / channel_id). A message that doesn't
    // parse as a board (no token / no expiries) is dropped without touching
    // state. Registered under every per-token key so whichever one the router
    // matches, this same function demuxes correctly.
    const handleUpdate = (raw: unknown) => {
      const feed = parseMarketProbabilitiesFeed(raw);
      if (!feed) return;
      // First real push proves the stream is live -- stop the warm-up clock.
      window.clearTimeout(warmupTimer);
      dispatchRef.current({ type: 'SET_FEED', feed });
    };

    for (const cfg of MARKETS) {
      const key = feedChannelKey(cfg.token);
      subscribeToChannel(key, handleUpdate);
      sendMessage(buildFeedSubscribeRequest(cfg.token));
    }

    return () => {
      window.clearTimeout(warmupTimer);
      for (const cfg of MARKETS) safeUnsubscribe(feedChannelKey(cfg.token));
    };
  }, [isConnected, sendMessage, subscribeToChannel, safeUnsubscribe, dispatchRef]);
}
